import React, { useState, useEffect, useRef } from 'react';
import { Check, X, PencilSimple } from '@phosphor-icons/react';

export interface EditableNumberFieldProps {
  icon: React.ComponentType<any>;
  label: string;
  field: string;
  value: any;
  isLoading?: boolean;
  isRequired?: boolean;
  onSave: (field: string, value: any) => void;
  isSaving?: boolean;
  showSuccess?: boolean;
  validationError?: string;
  placeholder?: string;
}

export const EditableNumberField: React.FC<EditableNumberFieldProps> = ({
  icon: Icon,
  label,
  field,
  value,
  isLoading = false,
  isRequired = false,
  onSave,
  isSaving = false,
  showSuccess = false,
  validationError,
  placeholder = "0.00"
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    const initialValue = value === null || value === undefined ? '' : String(value);
    console.log(`🔢 NumberField ${field} initialized with:`, initialValue);
    return initialValue;
  });
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Update local value when prop value changes (but not during editing)
  useEffect(() => {
    if (!isEditing) {
      const newValue = value === null || value === undefined ? '' : String(value);
      setLocalValue(newValue);
      console.log(`📊 NumberField ${field} updated to:`, newValue);
    }
  }, [value, isEditing, field]);
  
  const startEditing = () => {
    const editValue = value === null || value === undefined ? '' : String(value);
    setLocalValue(editValue);
    setIsEditing(true);
    console.log(`✏️ Started editing number ${field} with value:`, editValue);
  };
  
  const cancelEditing = () => {
    console.log(`❌ Cancelled editing number ${field}`);
    // Nullish check, niet truthy: een opgeslagen 0 moet 0 blijven en niet leeg
    // worden (leeg opslaan zet het veld op null).
    setLocalValue(value === null || value === undefined ? '' : String(value));
    setIsEditing(false);
  };
  
  const handleSave = () => {
    console.log(`💾 Saving number ${field} with value:`, localValue);
    const valueToSave = inputRef.current?.value ?? localValue ?? '';
    onSave(field, valueToSave);
    setIsEditing(false);
  };
  
  if (!isEditing) {
    return (
      <div className={`p-2 border-b border-neutral-primary/10 last:border-b-0 ${validationError ? 'bg-red-50/50' : ''}`}>
        {validationError && (
          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">
            <strong>Fout:</strong> {validationError}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon size={18} className="text-neutral-primary/60" />
            <span className="text-sm font-medium text-neutral-primary">
              {label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-primary min-w-[80px] text-right">
                {isLoading
                  ? 'Laden...'
                  : value === null || value === undefined || value === ''
                    ? 'N/A'
                    : value}
              </span>
              {showSuccess && (
                <div className="flex items-center gap-1 px-1 py-0.5 bg-status-good text-white rounded-sm text-xs">
                  <Check size={10} weight="bold" />
                </div>
              )}
            </div>
            <button
              onClick={startEditing}
              className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
              disabled={isLoading}
            >
              <PencilSimple size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`p-2 border-b border-neutral-primary/10 last:border-b-0 ${validationError ? 'bg-red-50/50' : ''}`}>
      {validationError && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">
          <strong>Fout:</strong> {validationError}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={localValue}
            onChange={(e) => {
              // De komma van het numerieke toetsenblok (BE/NL-indeling) wordt een
              // punt, anders werd ze geweigerd en kon je geen decimalen typen.
              const value = e.target.value.replace(',', '.');
              // Allow empty string, numbers, decimal point, and negative sign
              if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                setLocalValue(value);
              }
            }}
            onKeyDown={(e) => {
              // Geen tekenfilter op keyCode meer: die blokkeerde elke toets met
              // Shift erbij, en op een AZERTY-toetsenbord zijn de cijfers op de
              // bovenste rij nu juist Shift-combinaties (Shift+é = 2). Alleen de
              // numpad werkte daardoor. Het filteren gebeurt in onChange, dat
              // alle invoerwijzen dekt (typen, plakken, autofill).
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditing();
              }
            }}
            className={`border rounded px-2 py-1 text-sm focus:outline-hidden w-24 text-right ${
              validationError 
                ? 'border-red-300 focus:border-red-500' 
                : 'border-neutral-primary/20 focus:border-moss-primary'
            }`}
            placeholder={placeholder}
            autoFocus
          />
          <button
            type="button"
            disabled={isSaving}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            className={`transition-colors ${
              isSaving 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-status-good hover:text-status-good/80'
            }`}
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Check size={16} weight="bold" />
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cancelEditing();
            }}
            className="text-brick-primary hover:text-brick-primary/80 transition-colors"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};
